import React, { useState, useEffect } from "react";
import DeckGL from "@deck.gl/react";
import Map from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import AnimatedArcLayer from "./AnimatedArcLayer";
import Login from "./Login";

// --- Mapbox Configuration ---
const MAPBOX_ACCESS_TOKEN =
    "pk.eyJ1Ijoiam9lbGRlZ3JhZmYiLCJhIjoiY21obnRhMjNiMDQyMDJscHcwOTc5bzFpNSJ9.17vZSu2jnLvwbdKza7Nnbg";

// --- Animation Configuration ---
const ANIMATION_DURATION = 2000;

// Initial map view state
const INITIAL_VIEW_STATE = {
    longitude: -98,
    latitude: 39,
    zoom: 4,
    pitch: 45,
    bearing: 0,
};

function App() {
    const [data, setData] = useState([]);
    const [time, setTime] = useState(0);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [token, setToken] = useState(null);

    // Check for existing authentication on mount
    useEffect(() => {
        const verifyToken = async () => {
            const storedToken = localStorage.getItem("token");

            if (!storedToken) {
                setIsLoading(false);
                return;
            }

            try {
                const response = await fetch(
                    "https://stats.hdontap.com/api/verify",
                    {
                        headers: {
                            Authorization: `Bearer ${storedToken}`,
                        },
                    },
                );

                if (response.ok) {
                    setToken(storedToken);
                    setIsAuthenticated(true);
                } else {
                    // Token is invalid, clear it
                    localStorage.removeItem("token");
                    localStorage.removeItem("username");
                }
            } catch (error) {
                console.error("Token verification failed:", error);
                localStorage.removeItem("token");
                localStorage.removeItem("username");
            } finally {
                setIsLoading(false);
            }
        };

        verifyToken();
    }, []);

    // Data fetching (only when authenticated)
    useEffect(() => {
        if (!isAuthenticated || !token) return;

        const fetchData = async () => {
            try {
                const response = await fetch(
                    "https://stats.hdontap.com/api/viewer-data",
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    },
                );

                if (response.status === 401 || response.status === 403) {
                    // Token expired or invalid
                    handleLogout();
                    return;
                }

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const arcData = await response.json();
                const dataWithRandomOffset = arcData.map((d) => ({
                    ...d,
                    randomOffset: Math.random(),
                }));
                setData(dataWithRandomOffset);
            } catch (error) {
                console.error("Error fetching viewer data:", error);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, [isAuthenticated, token]);

    // Continuous animation loop
    useEffect(() => {
        if (!isAuthenticated) return;

        const animate = () => {
            const loopTime = ANIMATION_DURATION;
            const currentTime = (Date.now() % loopTime) / loopTime;
            setTime(currentTime);
            requestAnimationFrame(animate);
        };
        const animationFrame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrame);
    }, [isAuthenticated]);

    const handleLoginSuccess = (accessToken) => {
        setToken(accessToken);
        setIsAuthenticated(true);
    };

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("username");
        setToken(null);
        setIsAuthenticated(false);
        setData([]);
    };

    // Show loading state while verifying token
    if (isLoading) {
        return (
            <div
                style={{
                    width: "100vw",
                    height: "100vh",
                    background: "#1a1a2e",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#ffffff",
                    fontSize: "18px",
                }}
            >
                Loading...
            </div>
        );
    }

    // Show login if not authenticated
    if (!isAuthenticated) {
        return <Login onLoginSuccess={handleLoginSuccess} />;
    }

    const layers = [
        new AnimatedArcLayer({
            id: "animated-arc-layer",
            data,
            getSourcePosition: (d) => d.source,
            getTargetPosition: (d) => d.target,
            getSourceColor: (d) => d.color,
            getTargetColor: (d) => d.color,
            getWidth: 2,
            greatCircle: true,
            getHeight: 0.4,
            currentTime: time,
            getRandom: (d) => d.randomOffset,
        }),
    ];

    return (
        <div style={{ position: "relative", width: "100%", height: "100vh" }}>
            <DeckGL
                initialViewState={INITIAL_VIEW_STATE}
                controller={true}
                layers={layers}
            >
                <Map
                    mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
                    mapStyle="mapbox://styles/mapbox/dark-v10"
                />
            </DeckGL>

            {/* Logout Button */}
            <button
                onClick={handleLogout}
                style={{
                    position: "absolute",
                    top: "20px",
                    right: "20px",
                    padding: "10px 20px",
                    background: "#f47b20",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    zIndex: 1000,
                    transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                    e.target.style.background = "#e56b10";
                }}
                onMouseLeave={(e) => {
                    e.target.style.background = "#f47b20";
                }}
            >
                Logout
            </button>
        </div>
    );
}

export default App;
